import { useDispatch } from "react-redux";

import { useGetAnalysisTemplateDetail } from "@/api/analysis/useAnalysisAPI";
import {
  fetchDatasetDataBatch,
  fetchDatasetMetadataBatch
} from "@/api/dataset/datasetApi";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { Button } from "@/components/ui/button";
import Checkbox from "@/components/ui/checkbox";
import {
  buildFilterAsDatasetPayload,
  buildFilterFromResponse
} from "@/helpers/filterHelper";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import useLocalStorage from "@/hooks/store/useLocalStorage";
import useHandleClearView from "@/hooks/ui/useHandleClearView";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import {
  mapSpatialState,
  useAnalysisConfiguration
} from "@/hooks/useAnalysisConfiguration";
import { resetDatasetState } from "@/store/slice/datasetSlice";
import { resetSpatialState, setSpatialState } from "@/store/slice/spatialSlice";
import {
  setShowTemplateModal,
  toggleShowProjectList
} from "@/store/slice/uiSlice";
import { DatasetFilterPayload } from "@/types/dataset";
import { AnalysisConfigurationDetail } from "@/types/project";
import { Template } from "@/types/template";

import { enhanceWithAdminAreaData } from "./utils";

interface TemplateFooterProps {
  selectedTemplate: Template | null;
  setSelectedTemplate: (template: Template | null) => void;
}

const TEMPLATE_MODAL_DONT_SHOW_AGAIN_KEY = "template-modal-dont-show-again";

export default function TemplateFooter({
  selectedTemplate,
  setSelectedTemplate
}: TemplateFooterProps) {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { addFeature, deleteAllFeatures } = useMap();
  const { handleClearDataset } = useDataset();
  const { handleCloseDetailView } = useHandleClearView();

  const { data: templateDetail } = useGetAnalysisTemplateDetail(
    selectedTemplate?.id || "",
    { enabled: !!selectedTemplate?.id }
  );

  const { data: userProfile } = useGetUserProfileAPI();

  const { processDatasets } = useAnalysisConfiguration({});

  const [dontShowAgain, setDontShowAgain] = useLocalStorage(
    TEMPLATE_MODAL_DONT_SHOW_AGAIN_KEY,
    false
  );

  const handleSelectTemplate = async () => {
    if (!templateDetail) return;

    handleCloseDetailView();
    // Clear current data before open project
    handleClearDataset();
    deleteAllFeatures();
    dispatch(resetDatasetState());
    dispatch(resetSpatialState());

    try {
      dispatch(setShowTemplateModal(false));
      setSelectedTemplate(null);

      showToast({
        type: EToastType.LOADING,
        autoClose: 3000,
        message: (
          <div>
            <span className="text-sm text-txtHeading">
              Please wait the template is being loaded.
            </span>
          </div>
        )
      });
      const datasetIds = templateDetail.configuration.datasets
        .filter((dataset) => dataset.available)
        .map((availableDataset) => ({
          id: availableDataset.id?.split("_")?.[0],
          filters: availableDataset.attributes
            .filter((datasetAttribute) => datasetAttribute.filter?.criteria)
            .map<DatasetFilterPayload>((existingDatasetAttribute) => ({
              id: existingDatasetAttribute.id.split("_")?.[0],
              filter: buildFilterAsDatasetPayload(
                buildFilterFromResponse(existingDatasetAttribute.filter!),
                userProfile?.timezone
              )
            })),
          spatialAggregationType:
            availableDataset.spatial_aggregation_type ?? ""
        }));

      const [datasetsDetail, datasetsData] = await Promise.all([
        fetchDatasetMetadataBatch(datasetIds),
        fetchDatasetDataBatch(datasetIds)
      ]);

      const analysisConfigurationDetail: AnalysisConfigurationDetail = {
        id: templateDetail.id,
        name: templateDetail.title,
        description: templateDetail.short_description,
        ...templateDetail.configuration,
        datasetsDetail,
        datasetsData
      };
      const adminAreaId = analysisConfigurationDetail.area_input?.adm_area_id;

      // Enhance with admin area data if needed, otherwise use original
      const finalConfigurationDetail = adminAreaId
        ? await enhanceWithAdminAreaData(analysisConfigurationDetail)
        : analysisConfigurationDetail;

      // Clear current data and add new
      processDatasets(finalConfigurationDetail);

      // Set data for spatial config
      const spatialData = mapSpatialState(finalConfigurationDetail, addFeature);

      dispatch(setSpatialState(spatialData));
      dispatch(toggleShowProjectList(false));

      showToast({
        type: EToastType.SUCCESS,
        autoClose: 3000,
        message: (
          <div>
            <span className="text-sm text-txtHeading">
              Template has been successfully loaded
            </span>
          </div>
        )
      });
    } catch (error) {
      console.error("Error loading template:", error);
      showToast({
        type: EToastType.ERROR,
        autoClose: 3000,
        message: (
          <div>
            <span className="text-sm text-txtHeading">
              Sorry, we couldn’t load the project. Please try again.
            </span>
          </div>
        )
      });
    }
  };

  const handleBack = () => {
    setSelectedTemplate(null);
  };

  const handleDontShowAgainChange = (value: boolean) => {
    setDontShowAgain(value);
  };

  return (
    <div className="flex items-center w-full gap-2">
      {selectedTemplate ? (
        <>
          <Button
            variant="outline"
            className="!w-full rounded-xl"
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="default"
            className="!w-full rounded-xl"
            onClick={handleSelectTemplate}
          >
            Select template
          </Button>
        </>
      ) : (
        <>
          <Checkbox
            id="dont-show-again"
            onChange={handleDontShowAgainChange}
            checked={dontShowAgain}
            data-testid="dont-show-again-checkbox"
          />
          <label htmlFor="dont-show-again" className="text-sm text-neutral-600">
            Don't show this again
          </label>
        </>
      )}
    </div>
  );
}
